import React, { ReactNode, useMemo } from "react";
import CssBaseline from "@mui/material/CssBaseline";
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  PaletteColorOptions,
} from "@mui/material/styles";

import { palette } from "./palette";
import { shadows } from "./shadows";
import { overrides } from "./overrides";
import { CustomTypography, typography } from "./typography";
import { customShadows } from "./custom-shadows";
import { CustomPalette } from "./palette";
import { CustomShadows } from "./custom-shadows";

// ----------------------------------------------------------------------
declare module "@mui/material/styles" {
  interface Theme {
    customShadows: CustomShadows;
    palette: CustomPalette;
    typography: CustomTypography;
  }
  interface PaletteColor {
    lighter?: string;
    light: string;
    main: string;
    dark: string;
    darker?: string;
    contrastText: string;
  }
  interface Palette {
    disabled: PaletteColor;
    pink: PaletteColor;
    emerald: PaletteColor;
    revised: PaletteColor;
  }
  interface PaletteOptions {
    disabled?: PaletteColorOptions;
    emerald?: PaletteColorOptions;
    pink?: PaletteColorOptions;
    revised?: PaletteColorOptions;
  }

  interface BreakpointOverrides {
    xxl: true;
    xxxl: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    disabled: true;
    grey: true;
    pink: true;
    emerald: true;
    revised: true;
    default: true;
  }
}
declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    disabled: true;
    grey: true;
    pink: true;
    revised: true;
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    mono: true;
    caption2: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsColorOverrides {
    "grey.800": true;
    disabled: true;
    emerald: true;
    pink: true;
    revised: true;
    grey: true;
  }
}

declare module "@mui/material/Tabs" {
  interface TabsPropsIndicatorColorOverrides {
    error: true;
  }
}
declare module "@mui/material/TextField" {
  interface TextFieldPropsColorOverrides {
    emerald: true;
    pink: true;
    revised: true;
  }
}

export const ApplicationFormCustomThemeContent = {
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: "bold", // Custom font weight for label
          fontSize: "1.25rem", // Custom font size for label
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          marginTop: "0.3rem", // Custom margin for ToggleButtonGroup
        },
        grouped: {
          paddingTop: "5px",
          paddingBottom: "5px",
        },
      },
    },
  },
};

export const CashifyTheme = {
  palette: palette(),
  typography,
  shadows: shadows(),
  customShadows: customShadows(),
  shape: { borderRadius: 8 },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1800,
      xxxl: 2100,
    },
  },
};

export default function ThemeProvider({ children }: { children: ReactNode }) {
  const memoizedValue = useMemo(() => CashifyTheme, []);

  const theme = createTheme(memoizedValue);

  theme.components = overrides(theme);

  return (
    <MUIThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MUIThemeProvider>
  );
}
